import { holatVar } from "./palette";
import { BOSH_USLUB, ulushFoiz, yumaloq } from "./scale";

export interface HistogramBin {
	/** Bucket yorlig'i (`"5-15"`), o'q ostida chiqadi. */
	label: string;
	count: number;
	/** Ajratib ko'rsatiladigan bin (masalan chegaradan oshganlar). */
	highlight?: boolean;
}

export interface HistogramProps {
	bins: HistogramBin[];
	/** Standart — `holatVar("kelgan")`. */
	color?: string;
	/** Standart — `holatVar("kelmagan")`. */
	highlightColor?: string;
	/** Bin markazidagi vertikal uzuq belgi (masalan mediana). */
	marker?: { index: number; label: string };
	/** Chizma maydoni balandligi, px. */
	height?: number;
	/** Bo'sh ro'yxat matni (reja imzosidan kengaytirilgan). */
	empty?: string;
}

/**
 * Gistogramma (sof CSS) — tartiblangan bucketlar, balandlik eng katta
 * sanoqqa nisbatan.
 *
 * ⚠️ **Sanoq har bin ustida MATN sifatida ham turadi** — rang
 * (`highlight`) va balandlik yagona kanal bo'lmasin.
 *
 * ⚠️ `data-bin*` — TEST KONTRAKTI.
 */
export function Histogram({
	bins,
	color = holatVar("kelgan"),
	highlightColor = holatVar("kelmagan"),
	marker,
	height = 120,
	empty,
}: HistogramProps) {
	if (bins.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	const max = Math.max(0, ...bins.map((b) => b.count));
	const n = bins.length;
	const markerBor = marker !== undefined && marker.index >= 0 && marker.index < n;

	return (
		<div>
			<div
				role="img"
				aria-label={bins.map((b) => `${b.label}: ${b.count}`).join(", ")}
				style={{
					height: `${height}px`,
					paddingTop: 16,
					borderBottom: "1px solid var(--chart-grid)",
				}}
			>
				<div className="relative flex h-full items-stretch gap-0.5">
					{bins.map((bin) => (
						<div key={bin.label} className="flex min-w-0 flex-1 flex-col justify-end">
							<div
								data-bin-count
								className="text-center text-[10px] tabular-nums"
								style={{ color: "var(--chart-ink)" }}
							>
								{bin.count}
							</div>
							<div
								data-bin
								data-highlight={bin.highlight ? "" : undefined}
								title={`${bin.label}: ${bin.count}`}
								className="w-full shrink-0 rounded-t-sm"
								style={{
									height: `${ulushFoiz(bin.count, max)}%`,
									backgroundColor: bin.highlight ? highlightColor : color,
								}}
							/>
						</div>
					))}

					{markerBor ? (
						<div
							data-bin-marker
							aria-hidden="true"
							className="pointer-events-none absolute top-0 bottom-0"
							style={{
								left: `${yumaloq(((marker.index + 0.5) / n) * 100)}%`,
								borderLeft: "1px dashed var(--chart-ink)",
							}}
						>
							<span
								className="absolute -top-4 left-1 whitespace-nowrap text-[10px]"
								style={{ color: "var(--chart-ink)" }}
							>
								{marker.label}
							</span>
						</div>
					) : null}
				</div>
			</div>

			<div className="mt-1 flex gap-0.5">
				{bins.map((bin) => (
					<div
						key={bin.label}
						className="min-w-0 flex-1 truncate text-center text-[10px]"
						style={{ color: "var(--chart-axis)" }}
					>
						{bin.label}
					</div>
				))}
			</div>
		</div>
	);
}
